import { CalendarDays, Wallet, Target } from "lucide-react";
import { formatINR } from "@/lib/api";
import { TOPIC_LABELS, monthLabel } from "@/lib/aiSummaries";

const icons = { attendance: CalendarDays, payroll: Wallet, performance: Target };
const pct = (v) => v == null ? "Not available" : `${v}%`;

function entries(topic, data) {
  if (topic === "attendance") return [
    ["Recorded days", data.records], ["Recorded presence", pct(data.recorded_presence_percent)],
    ["Present / Late / Half-day", `${data.statuses.present} / ${data.statuses.late} / ${data.statuses.half_day}`],
    ["Absent / Leave", `${data.statuses.absent} / ${data.statuses.leave}`],
    ["Unmarked non-Sunday days", data.unmarked_non_sunday_employee_days],
  ];
  if (topic === "payroll") return [
    ["Payroll records", data.records], ["Earned gross", formatINR(data.earned_gross)],
    ["Deductions", formatINR(data.deductions)], ["Net payable", formatINR(data.net_pay)],
    ["Incomplete records", data.incomplete_records],
  ];
  return [["Performance records", data.records], ["Sales achievement", pct(data.sales.achievement_percent)],
    ["Collection achievement", pct(data.collection.achievement_percent)], ["Scheme achievement", pct(data.scheme.achievement_percent)],
    ["Employees without records", data.employees_without_records]];
}

export const SourceMetrics = ({ snapshot }) => {
  if (!snapshot) return null;
  return (
    <section className="space-y-4" data-testid="ai-source-metrics">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="font-heading text-base md:text-lg font-semibold" data-testid="ai-source-heading">Source totals</h2>
        <span className="text-xs text-stone-500" data-testid="ai-source-date">{monthLabel(snapshot.current.month)} · {snapshot.current.employees_in_period} employees in period</span>
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        {snapshot.topics.map((topic) => {
          const Icon = icons[topic], data = snapshot.current[topic];
          return <div key={topic} className={`ai-source ai-source-${topic}`} data-testid={`ai-source-${topic}`}>
            <h3 className="flex items-center gap-2 text-sm font-semibold mb-4" data-testid={`ai-source-${topic}-title`}><Icon className="w-4 h-4" />{TOPIC_LABELS[topic]}</h3>
            {entries(topic, data).map(([label, value], i) => <div key={label} className="flex items-baseline justify-between gap-3 py-1.5 text-sm" data-testid={`ai-${topic}-metric-${i}`}>
              <span className="text-stone-600">{label}</span><strong className="text-right tabular-nums break-words min-w-0">{value}</strong>
            </div>)}
            {!data.records && <p className="text-xs text-amber-800 mt-2" data-testid={`ai-${topic}-empty`}>No records for this selection.</p>}
          </div>;
        })}
      </div>
      <details className="text-xs text-stone-600" data-testid="ai-source-notes">
        <summary className="cursor-pointer hover:text-primary transition-colors" data-testid="ai-source-notes-toggle">Data basis & previous month</summary>
        <div className="mt-3 space-y-3" data-testid="ai-source-notes-content">
          {snapshot.topics.map((topic) => <p key={topic} data-testid={`ai-basis-${topic}`}><strong>{TOPIC_LABELS[topic]}:</strong> {snapshot.current[topic].basis} Previous month ({monthLabel(snapshot.previous.month)}): {snapshot.previous[topic].records} records.</p>)}
          {snapshot.notes.map((note, i) => <p key={note} data-testid={`ai-note-${i}`}>{note}</p>)}
          <p data-testid="ai-capture-time">Captured {new Date(snapshot.captured_at).toLocaleString("en-IN")}</p>
        </div>
      </details>
    </section>
  );
};